import { apiUrl } from "@/lib/apiUrl";
import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
  reported: boolean;
}

/**
 * Verifica se o erro é uma race condition inofensiva do Radix UI / DOM.
 * Esses erros ocorrem quando o React desmonta um componente enquanto o Radix UI
 * ainda está tentando remover/inserir nós DOM via portais.
 * NÃO causam perda de dados — apenas um artefato visual transitório.
 */
function isHarmlessDomError(error: Error): boolean {
  const msg = error?.message || "";
  const name = error?.name || "";
  return (
    name === "NotFoundError" ||
    msg.includes("removeChild") ||
    msg.includes("NotFoundError") ||
    msg.includes("The node to be removed is not a child of this node") ||
    msg.includes("O nó a ser removido não é filho deste nó") ||
    msg.includes("insertBefore") ||
    msg.includes("appendChild") ||
    msg.includes("Failed to execute 'removeChild'") ||
    msg.includes("Failed to execute 'insertBefore'")
  );
}

/**
 * Error Boundary global — NUNCA exibe stack trace ou mensagem técnica.
 * Exibe mensagem amigável e reporta o erro ao servidor silenciosamente.
 * Erros de removeChild (race condition do Radix UI) são silenciados sem mostrar tela de erro.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null, reported: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Erros inofensivos de DOM: NÃO mostrar tela de erro, deixar o React se recuperar
    if (isHarmlessDomError(error)) {
      return { hasError: false, error: null, errorId: null };
    }
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const harmless = isHarmlessDomError(error);

    // Log no console apenas em desenvolvimento
    if (process.env.NODE_ENV === "development") {
      if (harmless) {
        console.warn("[ErrorBoundary] Harmless DOM error silenced:", error.message);
      } else {
        console.error("[ErrorBoundary] Caught error:", error, info);
      }
    }

    // Reportar ao servidor silenciosamente (inclusive erros inofensivos, para rastreabilidade)
    this.reportError(error, info, harmless);
  }

  private async reportError(error: Error, info: React.ErrorInfo, harmless: boolean) {
    try {
      await fetch(apiUrl("/api/trpc/systemErrors.logPublicError"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          json: {
            errorType: "FRONTEND",
            errorMessage: `[${this.state.errorId || "HARMLESS"}] ${harmless ? "[HARMLESS] " : ""}${error.message || "Unknown error"}`.substring(0, 2000),
            errorStack: error.stack?.substring(0, 10000),
            componentStack: info.componentStack?.substring(0, 10000),
            url: window.location.href.substring(0, 512),
            userAgent: navigator.userAgent.substring(0, 512),
            context: {
              errorId: this.state.errorId,
              timestamp: new Date().toISOString(),
              harmless,
              source: "GlobalErrorBoundary",
            },
          },
        }),
      });
      this.setState({ reported: true });
    } catch {
      // Silencioso — nunca propagar erro do error boundary
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            {/* Ícone */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-amber-500" />
              </div>
            </div>

            {/* Título */}
            <h1 className="text-xl font-semibold text-gray-900 mb-3">
              Algo deu errado
            </h1>

            {/* Mensagem amigável — SEM stack trace */}
            <p className="text-gray-600 text-sm leading-relaxed mb-2">
              Ocorreu um problema inesperado ao carregar esta página.
              Isso pode ter sido causado por uma instabilidade temporária.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Tente recarregar a página. Se o problema persistir, entre em contato com o suporte.
            </p>

            {/* Código de protocolo */}
            {this.state.errorId && (
              <div className="bg-gray-50 rounded-lg px-4 py-3 mb-6 text-left">
                <p className="text-xs text-gray-500 mb-1">Código de protocolo</p>
                <p className="text-sm font-mono font-medium text-gray-700">{this.state.errorId}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Informe este código ao suporte se o problema persistir.
                </p>
              </div>
            )}

            {/* Ações */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.location.reload()}
                className={cn(
                  "flex items-center justify-center gap-2 px-4 py-3 rounded-lg w-full",
                  "bg-[#002F59] text-white",
                  "hover:opacity-90 cursor-pointer transition-opacity"
                )}
              >
                <RotateCcw size={16} />
                Recarregar página
              </button>

              <button
                onClick={() => window.location.href = "/"}
                className={cn(
                  "flex items-center justify-center gap-2 px-4 py-3 rounded-lg w-full",
                  "bg-gray-100 text-gray-700",
                  "hover:bg-gray-200 cursor-pointer transition-colors"
                )}
              >
                Voltar ao início
              </button>
            </div>

            {/* Status de reporte */}
            {this.state.reported && (
              <p className="text-xs text-gray-400 mt-4">
                O problema foi reportado automaticamente para a equipe técnica.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
